import { z } from 'zod';

/**
 * Three product tiers. Same codebase; tier-gated at runtime via license.
 */
export const Tier = z.enum(['core', 'pro', 'enterprise']);
export type Tier = z.infer<typeof Tier>;

/**
 * Feature flags gating tier-restricted behavior. The schema accepts any flag
 * in `config.enabledFeatures` at every tier; the runtime intersects with what
 * the license entitles. See SCHEMA.md § "enabledFeatures".
 */
export const FeatureFlag = z.enum([
  // Pro
  'theming',
  'multiProfile',
  'scheduledProfiles',
  'agentActions',
  'diagnosticOverlay',
  'broadcasts',
  'customTools',
  // Pro: biometric admin gesture. When the host (sideload APK or a
  // MobiControl WebView with the right bridge) exposes a biometric
  // primitive, the long-press exit corner uses it instead of (or in
  // addition to) the PIN dialog. Falls back to PIN when the bridge
  // isn't present (browsers, dev / demo).
  'biometricAuth',
  // Pro: pure-JS desktop-mode detection. Heuristic — fires when the
  // window goes wide-and-landscape past a desktop threshold. Catches
  // Samsung DeX + Microsoft Continuum + Chromebook tablet-mode, plus
  // any sufficiently large landscape window. Good enough for most;
  // imprecise edge cases (a sideways tablet on a stand) trip it.
  'dexDetectionBasic',
  // Pro: signage mode — hides launcher chrome + upsizes tiles for distance
  // viewing. A single wall tablet is an SMB use case, so it's Pro (the
  // tier mapping in src/tier/features.ts is the source of truth).
  'signageMode',
  // Enterprise (in-launcher)
  'remoteConfig',
  // Enterprise: pilot (canary) rollout. When entitled, a tile's `pilot`
  // block targets it to a subset of the fleet (percentage, daily ramp,
  // subnet, or device tag); without the flag the runtime ignores `pilot`
  // and shows the tile to everyone. See src/runtime/pilot.ts.
  'pilotRollout',
  'telemetryExport',
  'ssoDisplay',
  'auditLog',
  // Enterprise: precise DeX detection via the native bridge's
  // Configuration.uiMode reading. Only fires for genuine
  // UI_MODE_TYPE_DESK — won't trip on a stationary landscape tablet.
  // Requires the host APK / WebView shell to expose isDexMode() on
  // window.MobiLauncherBridge.
  'dexDetectionNative',
  // Enterprise (OEM device extensions)
  'oem.zebra',
  'oem.samsung',
  'oem.honeywell',
]);
export type FeatureFlag = z.infer<typeof FeatureFlag>;

/**
 * Feature flags that are NEVER granted implicitly — not by an empty
 * `enabledFeatures` ("use everything entitled"), not by the Builder's
 * "Enable all", not by the signup/webhook seed. They must be listed
 * explicitly in `enabledFeatures` to take effect.
 *
 * Why these are special: their mere presence changes how the launcher looks
 * or behaves, rather than just unlocking a capability that other config gates.
 * `signageMode` hides the header + footer and rescales the grid — so a tenant
 * who said "give me everything" and never asked for signage would otherwise
 * have their launcher chrome silently disappear. Opt-in only.
 */
export const OPT_IN_FEATURES: ReadonlySet<FeatureFlag> = new Set<FeatureFlag>(['signageMode']);

/**
 * Every feature flag except the opt-in-only ones — i.e. what "enable
 * everything" should actually turn on (the empty-`enabledFeatures` default,
 * the Builder's bulk Enable-all, and the signup/webhook seed all use this).
 */
export const BULK_ENABLE_FEATURES: readonly FeatureFlag[] = FeatureFlag.options.filter(
  (f) => !OPT_IN_FEATURES.has(f),
);

/**
 * Feature flags that USED to exist but have been retired from the product.
 * They're stripped from `enabledFeatures` at parse time (see the schema)
 * rather than rejected, so a config published while the flag was live keeps
 * loading — the launcher would otherwise fail the whole config over a single
 * now-unknown enum value, breaking already-deployed fleets. Genuinely unknown
 * flags (typos) are still rejected.
 *
 *  - `policyBlocklist`: the package blocklist. Removed because the launcher is
 *    a pure allowlist — it only renders configured tiles — so "blocking" a
 *    package you never surface was a no-op layered on a whitelist.
 */
export const RETIRED_FEATURE_FLAGS: ReadonlySet<string> = new Set<string>(['policyBlocklist']);
